import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: false,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const clearSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('currentOutlet');
};

// Silent refresh: on a 401, try to renew the token once and retry the request.
// Only redirect to login if the refresh itself fails (i.e. the session is truly gone).
// Normalise FastAPI validation errors: `detail` can be an ARRAY of objects
// ({type,loc,msg,...}). Many handlers do `toast.error(err.response.data.detail
// || '...')`, and a truthy object/array slips past the `||` and is handed to
// React as a child -> "React error #31" -> the whole app white-screens. Coerce
// `detail` to a string once, here, so every downstream handler is safe.
const coerceDetail = (d) => {
  if (d == null || typeof d === 'string') return d;
  if (Array.isArray(d)) {
    const msgs = d.map((x) => (x && (x.msg || x.message)) || '').filter(Boolean);
    return msgs.length ? msgs.join(', ') : 'Request failed';
  }
  if (typeof d === 'object') return d.msg || d.message || 'Request failed';
  return String(d);
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    if (error?.response?.data && 'detail' in error.response.data) {
      error.response.data.detail = coerceDetail(error.response.data.detail);
    }
    const original = error.config || {};
    const status = error?.response?.status;
    const url = original.url || '';
    const isAuthCall = url.includes('/auth/refresh') || url.includes('/auth/login');

    if (status === 401 && !original._retry && !isAuthCall) {
      original._retry = true;
      try {
        const token = localStorage.getItem('token');
        const r = await axios.post(`${API_URL}/api/auth/refresh`, {}, { headers: { Authorization: `Bearer ${token}` } });
        localStorage.setItem('token', r.data.access_token);
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${r.data.access_token}` };
        return api(original); // retry the original request with the fresh token
      } catch (e) {
        clearSession();
        if (!window.location.pathname.startsWith('/login')) window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    if (status === 401 && !isAuthCall) clearSession();
    return Promise.reject(error);
  }
);

export const auth = {
  login: (email, password) => api.post('/api/auth/login', { email, password }),
  me: () => api.get('/api/auth/me'),
};

export const tenantAPI = {
  getInfo: () => api.get('/api/tenant/info'),
  getOutlets: () => api.get('/api/tenant/outlets'),
  getOutlet: (id) => api.get(`/api/tenant/outlets/${id}`),
  createOutlet: (data) => api.post('/api/tenant/outlets', data),
  updateOutlet: (id, data) => api.put(`/api/tenant/outlets/${id}`, data),
  getFeatureFlags: () => api.get('/api/tenant/feature-flags'),
};

export const menuAPI = {
  getCategories: () => api.get('/api/menu/categories'),
  createCategory: (data) => api.post('/api/menu/categories', data),
  updateCategory: (id, data) => api.put(`/api/menu/categories/${id}`, data),
  getItems: (categoryId, outletId) =>
    api.get('/api/menu/items', { params: { category_id: categoryId, outlet_id: outletId } }),
  createItem: (data) => api.post('/api/menu/items', data),
  updateItem: (id, data) => api.put(`/api/menu/items/${id}`, data),
  deleteItem: (id) => api.delete(`/api/menu/items/${id}`),
};

export const cashierAPI = {
  getCashBalance: (outletId) => api.get('/api/cashier/cash-balance', { params: { outlet_id: outletId } }),
  getCashLedger: (outletId, shiftId) => api.get('/api/cashier/cash-ledger', { params: { outlet_id: outletId, shift_id: shiftId } }),
  cashIn: (outletId, amount, reason, managerPin) =>
    api.post('/api/cashier/cash-in', { outlet_id: outletId, amount, reason, manager_pin: managerPin }),
  cashOut: (outletId, amount, reason, managerPin) =>
    api.post('/api/cashier/cash-out', { outlet_id: outletId, amount, reason, manager_pin: managerPin }),
  deposit: (outletId, amount, bankName, bankReference, reason, managerPin) =>
    api.post('/api/cashier/deposit', {
      outlet_id: outletId,
      amount,
      bank_name: bankName,
      bank_reference: bankReference,
      reason,
      manager_pin: managerPin,
    }),
  getShiftSummary: (outletId, shiftId) => api.get('/api/cashier/shift-summary', { params: { outlet_id: outletId, shift_id: shiftId } }),
  createZReading: (outletId, countedCash, notes, idempotencyKey) =>
    // outlet_id is a QUERY param on this endpoint; sending it only in the body
    // returned 422 (missing outlet_id), which then crashed the UI.
    api.post('/api/cashier/z-reading', {
      counted_cash: countedCash,
      notes,
      idempotency_key: idempotencyKey,
    }, { params: { outlet_id: outletId } }),
  getZReading: (outletId, businessDate) => api.get('/api/cashier/z-reading', { params: { outlet_id: outletId, business_date: businessDate } }),
  getZReadings: (outletId, startDate, endDate, limit = 30) =>
    api.get('/api/cashier/z-readings', { params: { outlet_id: outletId, start_date: startDate, end_date: endDate, limit } }),
  getDayStatus: (outletId) => api.get('/api/cashier/day-status', { params: { outlet_id: outletId } }),
};

export const reportsAPI = {
  getDailySales: (outletId, startDate, endDate) =>
    api.get('/api/reports/sales/summary', { params: { outlet_id: outletId, start_date: startDate, end_date: endDate } }),
  getDaypart: (outletId, date) =>
    api.get('/api/reports/sales/by-hour', { params: { outlet_id: outletId, date } }),
  getCategorySales: (outletId, startDate, endDate) =>
    api.get('/api/reports/sales/by-category', { params: { outlet_id: outletId, start_date: startDate, end_date: endDate } }),
  getXReading: (outletId) => api.get('/api/bir/x-reading', { params: { outlet_id: outletId } }),
  getZReadings: (outletId, startDate, endDate) =>
    api.get('/api/reports/z-readings', { params: { outlet_id: outletId, start_date: startDate, end_date: endDate } }),
  exportSales: (outletId, startDate, endDate) =>
    api.get('/api/reports/export/sales', { params: { outlet_id: outletId, start_date: startDate, end_date: endDate }, responseType: 'blob' }),
};

export const crmAPI = {
  getCustomers: (search = '') => api.get('/api/crm/customers', { params: { search } }),
  createCustomer: (data) => api.post('/api/crm/customers', data),
  enrollLoyalty: (customerId) => api.post(`/api/crm/customers/${customerId}/loyalty/adjust`, { points: 0, reason: 'Enroll loyalty' }),
};

export default api;
